/**
 * Человеческие тексты для HTTP 409 (ТЗ задачи 6, 19, 21, 25).
 *
 * Backend отдаёт detail строкой либо объектом { code, ... }. Пользователю
 * показывается только message; технический code уходит в debug и на экран
 * попадает лишь в отладочных блоках.
 */

const DEPENDENCY_CODES = new Set([
  'zone_used_by_active_rules',
  'detector_required_by_active_rules',
]);

const SPECIAL_MESSAGES = {
  legacy_zone_delete_requires_zones_api:
    'Зона должна удаляться через редактор зон. Список зон перечитан.',
  legacy_people_runtime_locked:
    'Детекцию людей сейчас нельзя отключить: она используется текущей конфигурацией или правилами.',
};

const VERSION_MESSAGES = {
  rule_core: 'Правило было изменено другим действием. Обновите данные и повторите.',
  rule_schedule: 'Расписание было изменено другим действием. Обновите данные и повторите.',
  rule_retention: 'Сроки хранения были изменены другим действием. Обновите данные и повторите.',
  zone: 'Зона была изменена после открытия формы. Данные обновлены — повторите изменение.',
  detection_profile: 'Настройки детекций изменил другой администратор. Показаны актуальные настройки, повторите изменение.',
  change_request: 'Заявка относится к устаревшему состоянию камеры и не может быть применена.',
};

const DEPENDENCY_MESSAGES = {
  zone: 'Зону используют активные правила.',
  detection_profile: 'Детекцию используют активные правила.',
};

const STALE_MESSAGE = 'Заявка относится к устаревшему состоянию камеры. Применить её нельзя — попросите подать заявку заново.';

/**
 * Недокументированная форма 409-ответа (ТЗ, «Коды 409 у зон»): backend
 * иногда кладёт detail строкой, а dependent_rules/features/feature_id —
 * соседними полями на верхнем уровне data, а не внутри detail. Раньше
 * readDetail отбрасывал их для строкового detail целиком, из-за чего модалка
 * зависимостей (dependency-конфликт) получала пустой список правил.
 * Топ-уровневые поля — только фоллбэк: если у самого detail-объекта уже есть
 * своё значение поля, оно в приоритете.
 */
const readDetail = (error) => {
  const data = error?.response?.data || {};
  const raw = data.detail;
  const topLevelExtras = {};
  if (data.dependent_rules !== undefined) topLevelExtras.dependent_rules = data.dependent_rules;
  if (data.features !== undefined) topLevelExtras.features = data.features;
  if (data.feature_id !== undefined) topLevelExtras.feature_id = data.feature_id;

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return { code: raw.code || '', detail: { ...topLevelExtras, ...raw } };
  }
  if (typeof raw === 'string') {
    return { code: raw, detail: { code: raw, ...topLevelExtras } };
  }
  return { code: '', detail: { ...topLevelExtras } };
};

export const describeConflict = (error, context = 'rule_core') => {
  if (error?.response?.status !== 409) {
    return { kind: 'not_conflict', message: '', debug: '', detail: {} };
  }

  const { code, detail } = readDetail(error);
  const lower = code.toLowerCase();

  if (SPECIAL_MESSAGES[code]) {
    return { kind: 'unknown', message: SPECIAL_MESSAGES[code], debug: code, detail };
  }

  if (DEPENDENCY_CODES.has(code)) {
    return {
      kind: 'dependency',
      message: DEPENDENCY_MESSAGES[context] || 'Действие блокируют активные правила.',
      debug: code,
      detail: { ...detail, dependent_rules: Array.isArray(detail.dependent_rules) ? detail.dependent_rules : [] },
    };
  }

  if (lower.includes('stale') || lower.includes('change_request')) {
    return { kind: 'stale', message: STALE_MESSAGE, debug: code, detail };
  }

  // Любой другой 409 — версия/конкурентное изменение: перечитать и повторить.
  return {
    kind: context === 'change_request' ? 'stale' : 'version',
    message: context === 'change_request' ? STALE_MESSAGE : (VERSION_MESSAGES[context] || VERSION_MESSAGES.rule_core),
    debug: code,
    detail,
  };
};

/**
 * Текст конфликта версии без разбора ответа — для статусов, которые
 * describeConflict не трактует как 409 (например, 412 у профиля детекции).
 */
export const versionConflictMessage = (context = 'rule_core') =>
  VERSION_MESSAGES[context] || VERSION_MESSAGES.rule_core;
